import { z } from "zod";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const COMPANY_CATEGORIES = [
  { value: "ambulantePflege", label: "Ambulante Pflege / Pflegedienst" },
  { value: "stationaereEinrichtung", label: "Stationäre Einrichtung / Pflegeheim" },
  { value: "arztpraxis", label: "Arztpraxis" },
  { value: "zahnarztpraxis", label: "Zahnarztpraxis" },
  { value: "physiotherapie", label: "Physiotherapie" },
  { value: "ergotherapie", label: "Ergotherapie" },
  { value: "logopaedie", label: "Logopädie" },
  { value: "apotheke", label: "Apotheke" },
  { value: "sonstiges", label: "Sonstiges Gesundheitsunternehmen" },
] as const;

export type CompanyCategory = (typeof COMPANY_CATEGORIES)[number]["value"];

export const COMPANY_CATEGORY_VALUES = COMPANY_CATEGORIES.map((c) => c.value) as [
  CompanyCategory,
  ...CompanyCategory[],
];

export const EMPLOYMENT_TYPES = [
  { value: "vollzeit", label: "Vollzeit" },
  { value: "teilzeit", label: "Teilzeit" },
  { value: "minijob", label: "Minijob" },
  { value: "ausbildung", label: "Ausbildung" },
] as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number]["value"];

export const EMPLOYMENT_TYPE_VALUES = EMPLOYMENT_TYPES.map((t) => t.value) as [EmploymentType, ...EmploymentType[]];

export const JOB_TITLES_BY_CATEGORY: Record<CompanyCategory, string[]> = {
  ambulantePflege: [
    "Pflegefachkraft (m/w/d)",
    "Examinierte Pflegefachkraft (m/w/d)",
    "Altenpfleger/in",
    "Gesundheits- und Krankenpfleger/in",
    "Pflegehilfskraft (mit/ohne Ausbildung)",
    "Medizinische Fachangestellte/r (für Pflegeorganisation)",
    "Betreuungskraft §43b/53c",
    "Hauswirtschaftskraft",
    "Pflegeberater/in",
    "Pflegedienstleitung (PDL)",
    "Stellv. Pflegedienstleitung",
    "Qualitätsbeauftragte/r",
    "Einsatzleitung / Teamleitung",
    "Verwaltungsmitarbeiter/in Pflege",
    "Wundmanager/in",
    "Praxisanleiter/in",
    "Fahrer/in für ambulante Touren",
    "Sonstiges",
  ],
  stationaereEinrichtung: [
    "Pflegefachkraft (m/w/d)",
    "Altenpfleger/in",
    "Krankenpfleger/in",
    "Pflegehilfskraft (mit/ohne Ausbildung)",
    "Pflegeassistenz",
    "Betreuungskraft §43b/53c",
    "Wohnbereichsleitung",
    "Pflegedienstleitung (PDL)",
    "Einrichtungsleitung",
    "Stellv. Einrichtungsleitung",
    "Qualitätsmanagementbeauftragte/r",
    "Praxisanleiter/in",
    "Sozialdienstmitarbeiter/in",
    "Ergotherapeut/in (stationär)",
    "Physiotherapeut/in (stationär)",
    "Hauswirtschaftskraft",
    "Küchenpersonal",
    "Haustechniker/in",
    "Verwaltungsmitarbeiter/in",
    "Sonstiges",
  ],
  arztpraxis: [
    "Medizinische/r Fachangestellte/r (MFA)",
    "Arzthelfer/in",
    "Medizinische/r Fachwirt/in",
    "Praxismanager/in",
    "Arzt/Ärztin (verschiedene Fachrichtungen)",
    "Anmeldekraft",
    "Laborassistenz",
    "Röntgenassistenz",
    "DMP-Beauftragte/r",
    "Hygiene-Fachkraft Praxis",
    "Rezeptionskraft",
    "Assistenz für Blutabnahmen",
    "Sonstiges",
  ],
  zahnarztpraxis: [
    "Zahnmedizinische/r Fachangestellte/r (ZFA)",
    "Zahnmedizinische/r Prophylaxeassistent/in (ZMP)",
    "Zahnmedizinische/r Verwaltungsassistent/in (ZMV)",
    "Dentalhygieniker/in (DH)",
    "Zahntechniker/in",
    "Praxismanager/in",
    "Empfangskraft",
    "Assistenz am Behandlungsstuhl",
    "Abrechnungsmanager/in",
    "Kieferorthopädische/r Assistent/in",
    "Sonstiges",
  ],
  physiotherapie: [
    "Physiotherapeut/in",
    "Manualtherapeut/in",
    "Sportphysiotherapeut/in",
    "Lymphdrainage-Therapeut/in",
    "Osteopath/in",
    "Reha-/Krankengymnast/in",
    "Praxismanager/in",
    "Anmeldung/Empfangskraft",
    "Massage-Therapeut/in (medizinisch)",
    "Fitness-/Reha-Trainer/in (in Praxen mit Trainingsbereich)",
    "Sonstiges",
  ],
  ergotherapie: [
    "Ergotherapeut/in",
    "Fachergotherapeut/in (Geriatrie, Pädiatrie, Neurologie etc.)",
    "Handtherapeut/in",
    "Praxismanager/in",
    "Anmeldung/Empfangskraft",
    "Therapieassistenz",
    "Sonstiges",
  ],
  logopaedie: [
    "Logopäde/Logopädin",
    "Sprachtherapeut/in",
    "Atem-, Sprech- und Stimmtherapeut/in",
    "Praxismanager/in",
    "Anmeldung/Empfangskraft",
    "Therapieassistenz",
    "Sonstiges",
  ],
  apotheke: [
    "Apotheker/in",
    "Pharmazeutisch-technische/r Assistent/in (PTA)",
    "Pharmazeutisch-kaufmännische/r Angestellte/r (PKA)",
    "Apothekenhelfer/in",
    "Apothekenmanager/in",
    "Filialleitung Apotheke",
    "Rezeptur-/Labor-Mitarbeiter/in",
    "Botendienstfahrer/in",
    "Sonstiges",
  ],
  sonstiges: [
    "Medizinische/r Fachangestellte/r",
    "Rettungssanitäter/in",
    "Notfallsanitäter/in",
    "MTA – Medizinisch-technische/r Assistent/in",
    "Orthopädietechniker/in",
    "Sanitätshaus-Fachverkäufer/in",
    "Therapeut/in (diverse Bereiche)",
    "Sozialarbeiter/in",
    "Büro-/Verwaltungsmitarbeiter/in",
    "Hauswirtschaftspersonal",
    "Fahrer/in",
    "Sonstiges",
  ],
};

// ---------------------------------------------------------------------------
// Sub-schemas
// ---------------------------------------------------------------------------

export const fileMetaSchema = z.object({
  name: z.string(),
  size: z.number(),
  type: z.string(),
  previewUrl: z.string(),
});

export const positionSchema = z.object({
  jobTitle: z.string().min(1, "Bitte wählen Sie eine Berufsbezeichnung aus"),
  belongsToWorkingTimeModels: z.array(z.enum(EMPLOYMENT_TYPE_VALUES)).min(1, "Bitte wählen Sie mindestens ein Arbeitszeitmodell"),
  description: z.string().min(1, "Bitte geben Sie eine Stellenbeschreibung ein"),
  catchmentAreaKm: z.coerce.number().min(1, "Bitte geben Sie ein Einzugsgebiet ein").default(25),
  benefits: z.array(z.string()).default([]),
  applicantProfile: z.string().min(1, "Bitte beschreiben Sie die Anforderungen und Qualifikationen"),
});

export const locationSchema = z.object({
  categories: z
    .array(z.enum(COMPANY_CATEGORY_VALUES))
    .min(1, "Bitte wählen Sie mindestens eine Branche aus"),
  name: z.string().min(2, "Bitte geben Sie den Namen des Standorts ein"),
  street: z.string().min(2, "Bitte geben Sie die Straße ein"),
  postCode: z.string().min(4, "Bitte geben Sie die Postleitzahl ein"),
  placeName: z.string().min(2, "Bitte geben Sie den Ort ein"),
  contactName: z.string().min(2, "Bitte geben Sie den Namen des Ansprechpartners ein"),
  contactPhone: z.string().min(4, "Bitte geben Sie eine Telefonnummer ein"),
  contactEmail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
  additionalEmails: z.array(z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein")).default([]),
  positions: z.array(positionSchema).default([]),
});

// ---------------------------------------------------------------------------
// Section schemas (for per-step validation in the multi-step form)
// ---------------------------------------------------------------------------

/** Step 1 – Persönliche Daten */
export const personalDataSection = z.object({
  firstName: z.string().min(2, "Bitte geben Sie den Vornamen ein"),
  lastName: z.string().min(2, "Bitte geben Sie den Nachnamen ein"),
});

/** Step 3 – Standorte: at least one location, each with at least one category */
export const locationsSection = z.object({
  locations: z
    .array(
      z.object({
        categories: z
          .array(z.enum(COMPANY_CATEGORY_VALUES))
          .min(1, "Bitte wählen Sie mindestens eine Branche aus"),
      }),
    )
    .min(1, "Bitte fügen Sie mindestens einen Standort hinzu"),
});

/** Step 4 – Standortdaten: address & contact for every location */
export const locationDataSection = z.object({
  locations: z
    .array(
      z.object({
        name: z.string().min(2, "Bitte geben Sie den Namen des Standorts ein"),
        street: z.string().min(2, "Bitte geben Sie die Straße ein"),
        postCode: z.string().min(4, "Bitte geben Sie die Postleitzahl ein"),
        placeName: z.string().min(2, "Bitte geben Sie den Ort ein"),
        contactName: z.string().min(2, "Bitte geben Sie den Namen des Ansprechpartners ein"),
        contactPhone: z.string().min(4, "Bitte geben Sie eine Telefonnummer ein"),
        contactEmail: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein"),
        additionalEmails: z.array(z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein")).default([]),
      }),
    )
    .min(1),
});

/** Step 5 – Mitarbeiterbenefits */
export const benefitsSection = z.object({
  benefits: z.array(z.string()).default([]),
});

/** Step 6 – Stellendaten: every location must have at least one position */
export const positionsSection = z.object({
  locations: z
    .array(
      z.object({
        positions: z.array(positionSchema).min(1, "Bitte fügen Sie mindestens eine Stelle hinzu"),
      }),
    )
    .min(1),
});

export const IDENTITY_CHAR_LIMITS = {
  aboutUs: 2000,
  missionAndValues: 1000,
  whyWorkWithUs: 1000,
} as const;

/** Step 7 – Unternehmensidentität */
export const identitySection = z.object({
  aboutUs: z
    .string()
    .min(10, "Bitte beschreiben Sie Ihr Unternehmen in mindestens 5–10 Sätzen")
    .max(IDENTITY_CHAR_LIMITS.aboutUs, `Maximal ${IDENTITY_CHAR_LIMITS.aboutUs} Zeichen erlaubt`),
  missionAndValues: z
    .string()
    .min(10, "Bitte beschreiben Sie Ihre Werte und Kultur")
    .max(IDENTITY_CHAR_LIMITS.missionAndValues, `Maximal ${IDENTITY_CHAR_LIMITS.missionAndValues} Zeichen erlaubt`),
  whyWorkWithUs: z
    .string()
    .min(10, "Bitte beschreiben Sie, warum Mitarbeiter gerne bei Ihnen arbeiten")
    .max(IDENTITY_CHAR_LIMITS.whyWorkWithUs, `Maximal ${IDENTITY_CHAR_LIMITS.whyWorkWithUs} Zeichen erlaubt`),
});

/** Step 8 – Medien & Branding */
export const mediaSection = z.object({
  logoFiles: z.array(fileMetaSchema).default([]),
  mediaFiles: z.array(fileMetaSchema).default([]),
  ciColorPrimary: z.string().optional(),
});

/** Step 2 – Unternehmensdaten (billing / legal entity) */
export const companyDataSection = z.object({
  companyName: z.string().min(2, "Bitte geben Sie den Unternehmensnamen ein"),
  ceo: z.string().min(2, "Bitte geben Sie den Namen des Geschäftsführers ein"),
  street: z.string().min(2, "Bitte geben Sie die Straße ein"),
  postCode: z.string().min(4, "Bitte geben Sie die Postleitzahl ein"),
  placeName: z.string().min(2, "Bitte geben Sie den Ort ein"),
});

// ---------------------------------------------------------------------------
// Combined schema
// ---------------------------------------------------------------------------

export const onboardingSchema = z.object({
  // Step 1
  firstName: z.string().min(2, "Bitte geben Sie den Vornamen ein"),
  lastName: z.string().min(2, "Bitte geben Sie den Nachnamen ein"),

  // Steps 3 + 4 + 6 (locations with categories, addresses, contacts, positions)
  locations: z.array(locationSchema).min(1, "Bitte fügen Sie mindestens einen Standort hinzu"),

  // Step 5
  benefits: z.array(z.string()).default([]),

  // Step 7
  aboutUs: z
    .string()
    .min(10, "Bitte beschreiben Sie Ihr Unternehmen in mindestens 5–10 Sätzen")
    .max(IDENTITY_CHAR_LIMITS.aboutUs, `Maximal ${IDENTITY_CHAR_LIMITS.aboutUs} Zeichen erlaubt`),
  missionAndValues: z
    .string()
    .min(10, "Bitte beschreiben Sie Ihre Werte und Kultur")
    .max(IDENTITY_CHAR_LIMITS.missionAndValues, `Maximal ${IDENTITY_CHAR_LIMITS.missionAndValues} Zeichen erlaubt`),
  whyWorkWithUs: z
    .string()
    .min(10, "Bitte beschreiben Sie, warum Mitarbeiter gerne bei Ihnen arbeiten")
    .max(IDENTITY_CHAR_LIMITS.whyWorkWithUs, `Maximal ${IDENTITY_CHAR_LIMITS.whyWorkWithUs} Zeichen erlaubt`),

  // Step 8
  logoFiles: z.array(fileMetaSchema).default([]),
  mediaFiles: z.array(fileMetaSchema).default([]),
  ciColorPrimary: z.string().optional(),

  // Step 2
  companyName: z.string().min(2, "Bitte geben Sie den Unternehmensnamen ein"),
  ceo: z.string().min(2, "Bitte geben Sie den Namen des Geschäftsführers ein"),
  street: z.string().min(2, "Bitte geben Sie die Straße ein"),
  postCode: z.string().min(4, "Bitte geben Sie die Postleitzahl ein"),
  placeName: z.string().min(2, "Bitte geben Sie den Ort ein"),
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type OnboardingFormData = z.infer<typeof onboardingSchema>;
export type FileMetaData = z.infer<typeof fileMetaSchema>;
export type LocationFormData = z.infer<typeof locationSchema>;
export type PositionFormData = z.infer<typeof positionSchema>;

// ---------------------------------------------------------------------------
// Default missionAndValues (for react-hook-form initialization)
// ---------------------------------------------------------------------------

export const DEFAULT_POSITION: PositionFormData = {
  jobTitle: "",
  belongsToWorkingTimeModels: [],
  description: "",
  catchmentAreaKm: 25,
  benefits: [],
  applicantProfile: "",
};

export const DEFAULT_LOCATION: LocationFormData = {
  categories: [],
  name: "",
  street: "",
  postCode: "",
  placeName: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  additionalEmails: [],
  positions: [],
};

export const onboardingDefaultValues: Partial<OnboardingFormData> = {
  firstName: "",
  lastName: "",
  locations: [{ ...DEFAULT_LOCATION }],
  benefits: [],
  aboutUs: "",
  missionAndValues: "",
  whyWorkWithUs: "",
  logoFiles: [],
  mediaFiles: [],
  ciColorPrimary: "",
  companyName: "",
  ceo: "",
  street: "",
  postCode: "",
  placeName: "",
};
